import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  Zap, Brain, Globe, Target, AlertTriangle, CheckCircle,
  ChevronDown, ChevronUp, X, Loader2, Package, FileCode2,
  Map, HelpCircle, BarChart3, Sparkles, ArrowRight, Crown,
  Search, Shield, Database, Code2, Cpu, Eye
} from "lucide-react";
import { AppLayout } from "@/components/AppLayout";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Log Parsers ──────────────────────────────────────────────────────────────

function parseInsight(logs = []) {
  for (const log of logs) {
    const idx = log.indexOf('[INSIGHT]');
    if (idx !== -1) {
      try { return JSON.parse(log.slice(idx + 9).trim()); }
      catch { /* skip */ }
    }
  }
  return null;
}

function parseFindings(logs = []) {
  const results = [];
  for (const log of logs) {
    const idx = log.indexOf('[FINDING]');
    if (idx === -1) continue;
    const content = log.slice(idx + 9).trim();
    const [urlPart, statusPart, msgPart, fixablePart] = content.split(' | ');
    if (!urlPart) continue;
    results.push({
      url: urlPart.trim(),
      status: (statusPart || 'ok').trim(),
      message: (msgPart || '').trim(),
      isFixable: (fixablePart || '').trim() === 'fixable:true',
      id: urlPart.trim() + '_' + results.length,
    });
  }
  return results;
}

function parseFixableCount(logs = []) {
  for (const log of logs) {
    const m = log.match(/\[STEP 3\].*?(\d+)\s+auto-fixable/i);
    if (m) return parseInt(m[1], 10);
  }
  return null;
}

// ─── Live Thinking messages ───────────────────────────────────────────────────

const LIVE_THINKING = [
  "Initializing connection to server...",
  "Fetching homepage HTML structure...",
  "Extracting business entities...",
  "[INSIGHT] Analyzing local market...",
  "Checking Schema.org JSON-LD presence...",
  "[INSIGHT] Simulating customer queries in AI...",
  "Mapping H1/H2 heading structure...",
  "Identifying business intent...",
  "[INSIGHT] Evaluating visibility in ChatGPT & Perplexity...",
  "Scanning for NAP contact data...",
  "Checking trust signals (E-E-A-T)...",
  "[INSIGHT] Generating ideal Schema.org blueprint...",
  "Scanning for GA4 and GTM on pages...",
  "Analyzing FAQ and Case Study coverage...",
  "Evaluating internal linking patterns...",
  "Checking Open Graph & social metadata...",
  "Scanning robots.txt configuration...",
  "Analyzing page load performance signals...",
  "Checking mobile responsiveness indicators...",
  "Mapping canonical URL structure...",
  "Detecting duplicate content patterns...",
  "[INSIGHT] Profiling competitor landscape...",
  "Reviewing XML sitemap completeness...",
  "Analyzing backlink anchor text distribution...",
];

// ─── Synthesis steps — 12 steps to stretch across 3-5 minutes ─────────────────

const SYNTHESIS_STEPS = [
  { icon: Database, color: "#3B82F6", label: "Compiling crawl data from all pages..." },
  { icon: Search,   color: "#06B6D4", label: "Running GEO AI Visibility Discovery..." },
  { icon: Brain,    color: "#8B5CF6", label: "Identifying business niche & location..." },
  { icon: HelpCircle, color: "#F59E0B", label: "Generating customer persona queries..." },
  { icon: Eye,      color: "#EC4899", label: "Checking AI citation readiness..." },
  { icon: Target,   color: "#10B981", label: "Analyzing market context gaps..." },
  { icon: FileCode2, color: "#3B82F6", label: "Building custom Schema.org JSON-LD..." },
  { icon: Map,      color: "#8B5CF6", label: "Generating AI-optimized sitemap..." },
  { icon: Code2,    color: "#F59E0B", label: "Compiling ready-to-paste code fixes..." },
  { icon: Shield,   color: "#10B981", label: "Validating robots.txt & crawler access..." },
  { icon: Cpu,      color: "#EC4899", label: "Calculating final AI Visibility Score..." },
  { icon: Package,  color: "#3B82F6", label: "Packaging your complete diagnostic report..." },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const scoreColor = (v) => v >= 70 ? '#22C55E' : v >= 40 ? '#F59E0B' : '#EF4444';
const scoreLabel = (v) => v >= 70 ? 'Good visibility' : v >= 40 ? 'Partially invisible' : 'Critically invisible';

// ─── Components ───────────────────────────────────────────────────────────────

function AuditProgressBar({ progress, stage }) {
  const pct = Math.max(2, Math.min(99, progress));
  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-zinc-400 font-mono uppercase tracking-widest">
          {stage === 'synthesis' ? 'Generating report' :
           stage === 'crawling'  ? 'Scanning & analyzing' :
                                   'Initializing'}
        </span>
        <span className="text-xs font-mono text-zinc-300">{Math.round(pct)}%</span>
      </div>
      <div className="h-1 w-full rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{
            width: `${pct}%`,
            background: stage === 'synthesis'
              ? 'linear-gradient(90deg, #8B5CF6, #3B82F6)'
              : 'linear-gradient(90deg, #3B82F6, #06B6D4)',
            boxShadow: '0 0 8px rgba(59,130,246,0.6)',
          }}
        />
      </div>
    </div>
  );
}

function DNAScanner({ thinkMessage }) {
  return (
    <div className="flex flex-col items-center py-10">
      <div className="relative flex items-center justify-center mb-8">
        {[80, 56, 36].map((size, i) => (
          <div
            key={i}
            className="absolute rounded-full border"
            style={{
              width: size, height: size,
              borderColor: `rgba(59,130,246,${0.15 + i * 0.12})`,
              animation: `pulse ${1.8 + i * 0.4}s ease-in-out infinite`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center z-10"
          style={{ background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.5)' }}
        >
          <Brain className="w-5 h-5 text-blue-400" style={{ animation: 'pulse 1.2s ease-in-out infinite' }} />
        </div>
      </div>
      <p
        key={thinkMessage}
        className="text-sm text-zinc-300 font-mono text-center"
        style={{ animation: 'fadeInUp 0.4s ease-out forwards' }}
      >
        {thinkMessage}
      </p>
      <p className="text-xs text-zinc-600 mt-2">AI is scanning your business DNA...</p>
    </div>
  );
}

function DramaticCard({ insight, domain }) {
  if (!insight) return null;
  const score = insight.ai_visibility_score_preview ?? 50;
  const color = scoreColor(score);

  return (
    <div
      className="rounded-2xl p-6 sm:p-8 text-center"
      style={{
        background: 'linear-gradient(135deg, rgba(15,20,40,0.95), rgba(10,15,30,0.98))',
        border: '1px solid rgba(59,130,246,0.25)',
        boxShadow: '0 0 40px rgba(59,130,246,0.08)',
        animation: 'dramaticReveal 0.6s cubic-bezier(0.34,1.56,0.64,1) forwards',
      }}
    >
      <div className="flex items-center justify-center gap-2 mb-4">
        <Sparkles className="w-4 h-4 text-blue-400" />
        <span className="text-xs text-blue-400 uppercase tracking-widest font-semibold">Initial Identification</span>
      </div>

      {insight.site_type && insight.business_intent && (
        <p className="text-lg sm:text-xl font-bold text-white mb-2 leading-snug">
          Identified:{" "}
          <span className="text-blue-300">{insight.site_type}</span>
          {" "}&mdash;{" "}
          <span className="text-zinc-300">Goal: {insight.business_intent}</span>
        </p>
      )}

      <div className="inline-flex flex-col items-center my-5 p-4 rounded-2xl" style={{ background: `${color}10`, border: `1px solid ${color}30` }}>
        <span className="text-5xl font-black leading-none" style={{ color }}>{score}%</span>
        <span className="text-xs uppercase tracking-widest mt-1 font-semibold" style={{ color }}>
          AI Visibility Score
        </span>
      </div>

      <p className="text-sm text-zinc-400 max-w-sm mx-auto">
        Your business is{" "}
        <span className="font-semibold" style={{ color }}>{scoreLabel(score).toLowerCase()}</span>{" "}
        to AI models like GPT-4o and Claude.
      </p>

      {insight.top_3_hypothetical_issues?.length > 0 && (
        <div className="mt-4 text-left space-y-1.5 max-w-sm mx-auto">
          {insight.top_3_hypothetical_issues.map((issue, i) => (
            <div key={i} className="flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
              <span className="text-xs text-zinc-400">{issue}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FixableCounter({ count }) {
  if (!count || count === 0) return null;
  return (
    <div
      className="flex items-center justify-center gap-3 p-4 rounded-xl"
      style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)' }}
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(239,68,68,0.15)' }}>
        <Zap className="w-5 h-5 text-red-400" />
      </div>
      <div>
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-black tabular-nums" style={{ color: '#EF4444', textShadow: '0 0 20px rgba(239,68,68,0.4)' }}>
            {count}
          </span>
          <span className="text-sm font-semibold text-red-400">fixable issues</span>
        </div>
        <p className="text-xs text-zinc-500">Ready-to-paste fix code will be generated for each issue</p>
      </div>
      <Link to="/pricing" className="ml-auto shrink-0">
        <Button size="sm" className="text-xs font-semibold text-white px-3 py-1.5"
          style={{ background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)' }}>
          <Crown className="w-3 h-3 mr-1" /> PRO
        </Button>
      </Link>
    </div>
  );
}

function FindingItem({ finding, isNew }) {
  const isOk = finding.status === 'ok';
  return (
    <div
      className="flex items-start gap-2.5 py-2 px-3 rounded-lg"
      style={{
        background: isNew ? (isOk ? 'rgba(34,197,94,0.06)' : 'rgba(239,68,68,0.06)') : 'transparent',
        borderLeft: isNew ? `2px solid ${isOk ? '#22C55E' : '#EF4444'}` : '2px solid transparent',
        animation: 'slideInFinding 0.3s ease-out forwards',
      }}
    >
      {isOk
        ? <CheckCircle className="w-3.5 h-3.5 text-green-400 shrink-0 mt-0.5" />
        : <X className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />
      }
      <div className="flex-1 min-w-0">
        <span className="text-[11px] font-mono text-zinc-400 mr-2">{finding.url}</span>
        <span className="text-[11px] text-zinc-300">{finding.message}</span>
      </div>
      {finding.isFixable && (
        <span className="text-[9px] px-1.5 py-0.5 rounded shrink-0 font-semibold"
          style={{ background: 'rgba(59,130,246,0.15)', color: '#93C5FD', border: '1px solid rgba(59,130,246,0.2)' }}>
          AUTO-FIX
        </span>
      )}
    </div>
  );
}

function LiveFeed({ findings }) {
  const feedRef = useRef(null);
  const prevLen = useRef(0);

  useEffect(() => {
    if (feedRef.current && findings.length > prevLen.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
    prevLen.current = findings.length;
  }, [findings.length]);

  if (findings.length === 0) return null;
  const recent = [...findings].slice(-30);

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(10,15,25,0.8)' }}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-400" style={{ animation: 'pulse 1s ease-in-out infinite' }} />
          <span className="text-[11px] text-zinc-400 uppercase tracking-widest font-semibold">Live Discoveries</span>
        </div>
        <span className="text-[11px] font-mono text-zinc-500">{findings.length} checked</span>
      </div>
      <div ref={feedRef} className="max-h-56 overflow-y-auto py-1 px-1" style={{ scrollbarWidth: 'none' }}>
        {recent.map((f, i) => (
          <FindingItem key={f.id} finding={f} isNew={i >= recent.length - 2} />
        ))}
      </div>
    </div>
  );
}

/** Synthesis Stage — adaptive timing, last step waits for backend */
function SynthesisStage({ synthesisStep, elapsedSec }) {
  const isLastStepActive = synthesisStep === SYNTHESIS_STEPS.length - 1;
  const allDone = synthesisStep >= SYNTHESIS_STEPS.length;

  return (
    <div className="rounded-2xl p-6 sm:p-8" style={{ background: 'rgba(15,20,40,0.95)', border: '1px solid rgba(139,92,246,0.25)' }}>
      <div className="flex items-center gap-2 mb-6 justify-center">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(139,92,246,0.2)' }}>
          <Package className="w-4 h-4 text-purple-400" />
        </div>
        <span className="text-sm font-semibold text-purple-300 uppercase tracking-wider">
          Building Your Package
        </span>
      </div>

      <div className="space-y-3 mb-6">
        {SYNTHESIS_STEPS.map((step, i) => {
          const Icon = step.icon;
          const done = allDone ? true : i < synthesisStep;
          const active = allDone ? false : i === synthesisStep;
          return (
            <div
              key={i}
              className="flex items-center gap-3 p-3 rounded-lg transition-all duration-500"
              style={{
                background: active ? `${step.color}10` : done ? 'rgba(255,255,255,0.02)' : 'transparent',
                border: active ? `1px solid ${step.color}25` : '1px solid transparent',
                opacity: done ? 0.5 : active ? 1 : 0.35,
              }}
            >
              <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${step.color}15` }}>
                {done
                  ? <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                  : active
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: step.color }} />
                  : <Icon className="w-3.5 h-3.5 text-zinc-600" />
                }
              </div>
              <span className={`text-sm ${active ? 'text-white font-medium' : done ? 'text-zinc-500' : 'text-zinc-600'}`}>
                {step.label}
              </span>
              {active && (
                <span className="ml-auto text-[10px] font-mono" style={{ color: step.color }}>
                  processing...
                </span>
              )}
              {done && (
                <span className="ml-auto text-[10px] text-green-400 font-mono">done</span>
              )}
            </div>
          );
        })}
      </div>

      <div className="text-center">
        {isLastStepActive && (
          <p className="text-xs text-purple-300/70 animate-pulse">
            Finalizing your diagnostic package...
          </p>
        )}
        {!isLastStepActive && !allDone && (
          <p className="text-xs text-zinc-500 animate-pulse">
            AI is analyzing your market position and generating code fixes...
          </p>
        )}
        {elapsedSec > 30 && (
          <p className="text-[10px] text-zinc-600 mt-1">
            Deep analysis takes 2-4 minutes. Almost there...
          </p>
        )}
      </div>
    </div>
  );
}

function TechLogs({ logs, visible, onToggle }) {
  const logEndRef = useRef(null);
  useEffect(() => {
    if (visible) logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs, visible]);

  return (
    <div>
      <button onClick={onToggle}
        className="flex items-center gap-1.5 text-[11px] text-zinc-600 hover:text-zinc-400 transition-colors mx-auto">
        {visible ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {visible ? 'Hide technical logs' : 'Show technical logs'}
      </button>
      {visible && (
        <div className="mt-3 rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)', background: '#070B10' }}>
          <div className="flex items-center gap-2 px-4 py-2 border-b border-white/5">
            <div className="w-2 h-2 rounded-full bg-red-500" /><div className="w-2 h-2 rounded-full bg-yellow-500" /><div className="w-2 h-2 rounded-full bg-green-500" />
            <span className="text-[10px] text-zinc-600 ml-1 font-mono">audit_log</span>
          </div>
          <div className="h-48 overflow-y-auto p-3" style={{ scrollbarWidth: 'thin', scrollbarColor: '#333 transparent' }}>
            {(logs || []).map((log, i) => (
              <div key={i} className="text-[10px] font-mono text-zinc-500 leading-5 break-all">{log}</div>
            ))}
            <div ref={logEndRef} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CSS Keyframes ────────────────────────────────────────────────────────────

const KEYFRAMES = `
@keyframes dramaticReveal {
  from { opacity: 0; transform: scale(0.92) translateY(8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes slideInFinding {
  from { opacity: 0; transform: translateX(-6px); }
  to   { opacity: 1; transform: translateX(0); }
}
`;

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function RunningPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [status, setStatus]             = useState(null);
  const [error, setError]               = useState(null);
  const [cancelling, setCancelling]     = useState(false);
  const [insight, setInsight]           = useState(null);
  const [findings, setFindings]         = useState([]);
  const [displayedFindings, setDisplayedFindings] = useState([]);
  const [fixableCount, setFixableCount] = useState(0);
  const [stage, setStage]               = useState('scanning');
  const [showLogs, setShowLogs]         = useState(false);
  const [thinkIdx, setThinkIdx]         = useState(0);
  const [synthesisStep, setSynthesisStep] = useState(0);

  // Drip-feed queue
  const findingsQueueRef = useRef([]);
  const displayedIdxRef = useRef(0);

  // Track when synthesis started for elapsed time display
  const synthesisStartRef = useRef(null);
  const [synthesisElapsed, setSynthesisElapsed] = useState(0);

  // Track audit start time for minimum phase durations
  const auditStartRef = useRef(Date.now());
  // Track if backend has reported completion
  const backendCompletedRef = useRef(false);

  // Track if all findings have been drip-fed (or there are none to show)
  const allFindingsDisplayed = findings.length === 0 || displayedFindings.length >= findings.length;

  // Inject CSS once
  useEffect(() => {
    const elId = 'rp-keyframes';
    if (!document.getElementById(elId)) {
      const style = document.createElement('style');
      style.id = elId;
      style.textContent = KEYFRAMES;
      document.head.appendChild(style);
    }
  }, []);

  // Rotate live thinking text every 3.5s — runs during scanning AND crawling
  useEffect(() => {
    if (stage !== 'scanning' && stage !== 'crawling') return;
    const t = setInterval(() => setThinkIdx(i => (i + 1) % LIVE_THINKING.length), 3500);
    return () => clearInterval(t);
  }, [stage]);

  // ─── DRIP FEED: Show one finding every 12 seconds (deliberate pace) ──────────
  useEffect(() => {
    const interval = setInterval(() => {
      const queue = findingsQueueRef.current;
      const idx = displayedIdxRef.current;
      if (idx < queue.length) {
        setDisplayedFindings(queue.slice(0, idx + 1));
        displayedIdxRef.current = idx + 1;
      }
    }, 12000);
    return () => clearInterval(interval);
  }, []);

  // ─── SYNTHESIS: Advance steps — last step waits for backend completion ──────
  useEffect(() => {
    if (stage !== 'synthesis') return;
    if (synthesisStartRef.current === null) {
      synthesisStartRef.current = Date.now();
    }

    // Elapsed timer (every 5s)
    const elapsedTimer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - synthesisStartRef.current) / 1000);
      setSynthesisElapsed(elapsed);
    }, 5000);

    // Last step (index = length - 1) stays active until backend completes
    if (synthesisStep >= SYNTHESIS_STEPS.length - 1) {
      // Don't auto-advance — waits for 'completed' status to fast-forward
      return () => clearInterval(elapsedTimer);
    }

    // Step advancement: 20 seconds per step for first 9, 25s for steps 9-10
    const stepDuration = synthesisStep < 9 ? 20000 : 25000;
    const stepTimer = setTimeout(() => {
      setSynthesisStep(s => Math.min(s + 1, SYNTHESIS_STEPS.length - 1));
    }, stepDuration);

    return () => {
      clearInterval(elapsedTimer);
      clearTimeout(stepTimer);
    };
  }, [stage, synthesisStep]);

  // Cancel audit
  const handleCancel = async () => {
    if (!window.confirm('Cancel audit? Credit will be returned if progress < 50%.')) return;
    setCancelling(true);
    try {
      const res = await axios.post(`${API}/audit/${id}/cancel`);
      toast.success(res.data.refunded ? 'Audit cancelled. Credit returned.' : 'Audit cancelled.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel audit');
      setCancelling(false);
    }
  };

  // 1-second polling
  const poll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/audit/${id}/status`);
      const data = res.data;
      setStatus(data);

      const logs = data.logs || [];

      // Insight
      const parsed = parseInsight(logs);
      if (parsed && !insight) setInsight(parsed);

      // Findings — buffer into queue
      const newFindings = parseFindings(logs);
      if (newFindings.length > findings.length) {
        setFindings(newFindings);
        findingsQueueRef.current = newFindings;
      }

      // Fixable count
      const fc = parseFixableCount(logs);
      if (fc !== null) {
        setFixableCount(fc);
      } else {
        const fromFindings = newFindings.filter(f => f.isFixable).length;
        if (fromFindings > fixableCount) setFixableCount(fromFindings);
      }

      // ─── Stage transitions ─────────────────────────────────────────────
      // Minimum crawling duration before synthesis can start (90 seconds)
      const elapsedSinceStart = (Date.now() - auditStartRef.current) / 1000;
      const MIN_CRAWLING_DURATION = 90;

      // Only go to synthesis AFTER all findings are displayed on screen
      // AND minimum crawling duration has passed
      const allDisplayed = findingsQueueRef.current.length === 0 ||
                           displayedIdxRef.current >= findingsQueueRef.current.length;

      if (data.status === 'generating_report' || data.progress >= 85) {
        // Backend is generating report — transition to synthesis only if:
        // 1. All findings have been drip-fed to screen
        // 2. Minimum crawling time has elapsed (prevents "too fast" feel)
        if (allDisplayed && elapsedSinceStart >= MIN_CRAWLING_DURATION) {
          setStage(prev => prev === 'done' ? prev : 'synthesis');
        }
        // Otherwise stay in current stage — drip-feed / time will catch up
      } else if (newFindings.length > 0 || (parsed && data.progress > 15)) {
        // Only go to crawling if we haven't reached synthesis yet
        setStage(prev => (prev === 'synthesis' || prev === 'done') ? prev : 'crawling');
      }

      // Completion — backend done, fast-forward synthesis and redirect
      if (data.status === 'completed' && !backendCompletedRef.current) {
        backendCompletedRef.current = true;
        // If we're still in crawling/scanning, jump to synthesis briefly
        if (stage !== 'synthesis' && stage !== 'done') {
          setStage('synthesis');
        }
        // Fast-forward synthesis to last step complete, then redirect
        setSynthesisStep(SYNTHESIS_STEPS.length);
        setTimeout(() => {
          setStage('done');
          setTimeout(() => navigate(`/report/${id}`), 1500);
        }, 2000);
      }
      if (data.status === 'failed') setError(data.step || 'Audit failed');
      if (data.status === 'cancelled') setError('Audit was cancelled');
    } catch {
      // silent — interval will retry
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, insight, findings.length, fixableCount]);

  useEffect(() => {
    poll();
    const interval = setInterval(poll, 1000);
    return () => clearInterval(interval);
  }, [poll]);

  // Smooth progress — cap per stage so it doesn't jump ahead
  const rawProgress   = status?.progress ?? status?.percent ?? 0;
  const uiProgress    = stage === 'done'      ? 100 :
                         stage === 'synthesis'  ? Math.max(55, Math.min(55 + (synthesisStep / SYNTHESIS_STEPS.length) * 40, 95)) :
                         stage === 'crawling'   ? Math.min(rawProgress, 55) :
                                                  Math.min(rawProgress, 20);
  const progress      = uiProgress;
  const domainName = status?.domain || 'your domain';
  const logs       = status?.logs || [];

  useEffect(() => {
    document.title = "Analyzing " + domainName + " – ScoreCite";
    return () => { document.title = "ScoreCite"; };
  }, [domainName]);

  // ─── Error state ───────────────────────────────────────────────────────────
  if (error) {
    return (
      <AppLayout>
        <div className="max-w-xl mx-auto px-4 py-20 text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(239,68,68,0.1)' }}>
            <X className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Audit failed</h2>
          <p className="text-zinc-400 text-sm mb-6">{error}</p>
          <Button onClick={() => navigate('/dashboard')} className="text-white" style={{ background: '#3B82F6' }}>
            Back to Dashboard
          </Button>
        </div>
      </AppLayout>
    );
  }

  // ─── Done state ────────────────────────────────────────────────────────────
  if (stage === 'done') {
    return (
      <AppLayout>
        <div className="max-w-xl mx-auto px-4 py-20 text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(34,197,94,0.1)' }}>
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Report ready!</h2>
          <p className="text-zinc-400 text-sm animate-pulse">Redirecting to report...</p>
        </div>
      </AppLayout>
    );
  }

  // ─── Main render ───────────────────────────────────────────────────────────
  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12 space-y-6">

        {/* Header */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-zinc-500 font-mono uppercase tracking-widest">
              {stage === 'synthesis' ? 'Generating report' :
               stage === 'crawling'  ? 'Analysis in progress' :
                                       'Initializing scanner'}
            </p>
            <button
              onClick={handleCancel}
              disabled={cancelling}
              className="text-[11px] text-zinc-600 hover:text-zinc-400 transition-colors disabled:opacity-40"
            >
              {cancelling ? 'Cancelling...' : 'Cancel'}
            </button>
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-white mb-4 truncate">
            {domainName}
          </h1>
          <AuditProgressBar progress={progress} stage={stage} />
        </div>

        {/* ── ETAP 1: Scanner + Dramatic Card ── */}
        {stage === 'scanning' && !insight && (
          <div className="rounded-2xl overflow-hidden"
            style={{ background: 'rgba(10,15,25,0.8)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <DNAScanner thinkMessage={LIVE_THINKING[thinkIdx]} />
          </div>
        )}

        {/* Dramatic Card — appears when [INSIGHT] arrives */}
        {insight && <DramaticCard insight={insight} domain={domainName} />}

        {/* ── ETAP 2: Live Discoveries (drip-fed, 12s/item) + analysis strip ── */}
        {(stage === 'crawling' || (stage === 'synthesis' && !allFindingsDisplayed)) && (
          <>
            {/* Animated analysis indicator — provides continuous visual feedback */}
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl"
              style={{ background: 'rgba(59,130,246,0.05)', border: '1px solid rgba(59,130,246,0.12)' }}>
              <Loader2 className="w-4 h-4 animate-spin text-blue-400 shrink-0" />
              <span
                key={thinkIdx}
                className="text-xs text-blue-300/80 font-mono"
                style={{ animation: 'fadeInUp 0.4s ease-out' }}
              >
                {LIVE_THINKING[thinkIdx]}
              </span>
            </div>
            <FixableCounter count={fixableCount} />
            <LiveFeed findings={displayedFindings} />
          </>
        )}

        {/* Scanning stage with insight but no findings yet */}
        {stage === 'scanning' && insight && (
          <div className="text-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-zinc-600 mx-auto mb-2" />
            <p className="text-xs text-zinc-600">Scanning next pages...</p>
          </div>
        )}

        {/* ── ETAP 3: Synthesis — only after ALL findings are displayed ── */}
        {stage === 'synthesis' && allFindingsDisplayed && (
          <>
            <FixableCounter count={fixableCount} />
            <SynthesisStage synthesisStep={synthesisStep} elapsedSec={synthesisElapsed} />
          </>
        )}

        {/* ── Technical Logs ── */}
        <div className="pt-2">
          <TechLogs logs={logs} visible={showLogs} onToggle={() => setShowLogs(v => !v)} />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-white/5">
          <div className="flex items-center gap-1.5">
            <Globe className="w-3.5 h-3.5 text-zinc-600" />
            <span className="text-[11px] text-zinc-600 font-mono">{domainName}</span>
          </div>
          <span className="text-[10px] text-zinc-700 font-mono">{id?.slice(0, 8)}</span>
        </div>

      </div>
    </AppLayout>
  );
}
